import React, { useState, useEffect } from 'react';
import { getQuittances, downloadQuittancePdf } from '../services/assujettiService';

interface Quittance {
  id: string;
  reference: string;
  period: string;
  establishment_name?: string;
  amount_usd: number;
  amount_cdf: number;
  status: string;
}

function statusBadge(s: string) {
  const v = s?.toLowerCase() || '';
  if (v.includes('genere') || v.includes('paid') || v === 'generated') return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Generee</span>;
  if (v.includes('pend')) return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">En attente</span>;
  return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{s}</span>;
}

export default function V4QuittancesPage() {
  const [rows, setRows] = useState<Quittance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    setLoading(true);
    getQuittances(page, pageSize)
      .then((r: any) => {
        setRows(r.data ?? []);
        setTotal(r.total ?? 0);
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [page]);

  const handleDownload = async (id: string) => {
    try {
      const blob = await downloadQuittancePdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quittance-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e.message);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading) return <div className="max-w-7xl mx-auto py-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" /><p className="mt-4 text-gray-500">Chargement...</p></div>;
  if (error) return <div className="max-w-7xl mx-auto py-10"><div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div></div>;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Quittances</h1>
      <p className="text-gray-500 text-sm mb-6">Certificats de paiement FPT</p>

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Aucune quittance trouvee.</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Reference', 'Periode', 'Etablissement', 'Montant', 'Statut', 'Action'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4 text-sm font-mono">{r.reference}</td>
                    <td className="px-5 py-4 text-sm">{r.period}</td>
                    <td className="px-5 py-4 text-sm">{r.establishment_name || '-'}</td>
                    <td className="px-5 py-4 text-sm">{r.amount_usd?.toLocaleString()} USD {r.amount_cdf ? `+ ${r.amount_cdf.toLocaleString()} CDF` : ''}</td>
                    <td className="px-5 py-4">{statusBadge(r.status)}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => handleDownload(r.id)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Telecharger</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">Page {page} sur {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">Precedent</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">Suivant</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
